import "@app/App.css";
import { Popover, PopoverContent, PopoverTrigger } from "@comps/ui/popover";
import { ObjectDto, ObjectInfos } from "@dto/objects";
import { updateStorageSelection } from "@utils/localStorage";
import { TW_ObjectMenuButton, TW_ObjectMenuButtonIcon } from "@utils/twClasses";
import { EllipsisVertical } from "lucide-react";
import { useState } from "react";
import { useModelStore } from "src/store";
import ObjectMenuAddFolderItem from "./ObjectMenuAddFolderItem";
import ObjectMenuDeleteItem from "./ObjectMenuDeleteItem";
import ObjectMenuDuplicateItem from "./ObjectMenuDuplicateItem";
import ObjectMenuEditItem from "./ObjectMenuEditItem";

interface ObjectMenuButtonProps {
  objectInfo: ObjectInfos;
  showIcon?: boolean;
  // Rights on the shared object definition; the personal folder item is not
  // gated by it
  canEditObject?: boolean;
}

function ObjectMenuButton({ objectInfo, showIcon = false, canEditObject = true }: ObjectMenuButtonProps) {

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const objectInfos = useModelStore((state) => state.objectInfos);
  const sharedObjectInfos = useModelStore((state) => state.sharedObjectInfos);
  const selectedObjectInfos = useModelStore((state) => state.selectedObjectInfos);
  const selectedView = useModelStore((state) => state.selectedView);
  const setObjectInfos = useModelStore((state) => state.updateObjectInfos);
  const workspace = useModelStore((state) => state.selectedWorkspace);

  const deleteObjectFromState = () => {
    const newObjectInfos = objectInfos.filter(o => o.object._key !== objectInfo.object._key);
    let newSelectedObjectInfos = null;
    if (newObjectInfos.length > 0) {
      newSelectedObjectInfos = newObjectInfos[0];
    }
    let newSelectedView = null;
    if (newSelectedObjectInfos && newSelectedObjectInfos.views && newSelectedObjectInfos.views.length > 0) {
      newSelectedView = newSelectedObjectInfos.views[0];
    }
    setObjectInfos(newObjectInfos, newSelectedObjectInfos, newSelectedView, null, sharedObjectInfos);
    updateStorageSelection(workspace?._key, newSelectedObjectInfos?.object?._key, newSelectedView?._key, null);
  }

  const deleteObject = () => {
    deleteObjectFromState();
    setIsPopoverOpen(false);
  }

  const updateObject = (object: ObjectDto) => {
    const newObjectInfos = objectInfos.map((o: ObjectInfos) => {
      if (o.object._key === object._key) {
        o.object = object;
      }
      return o;
    });
    let newSelectedObjectInfos = null;
    if (selectedObjectInfos?.object._key === object._key) {
      newSelectedObjectInfos = newObjectInfos.find(o => o.object._key === object._key) || null;
    }
    let newSelectedView = null;
    if (newSelectedObjectInfos && newSelectedObjectInfos.views) {
      newSelectedView = newSelectedObjectInfos.views.find(v => v._key === selectedView?._key) || null;
      if (!newSelectedView && newSelectedObjectInfos.views.length > 0) {
        newSelectedView = newSelectedObjectInfos.views[0];
      }
    }
    setObjectInfos(newObjectInfos, newSelectedObjectInfos, newSelectedView, null, sharedObjectInfos);
    updateStorageSelection(workspace?._key, newSelectedObjectInfos?.object?._key, newSelectedView?._key, null);
  }

  const objectUpdated = (object: ObjectDto) => {
    updateObject(object);
    setIsPopoverOpen(false);
  }

  const duplicateObject = (newObjectInfo: ObjectInfos) => {
    const newObjectInfos = [...objectInfos, newObjectInfo];
    newObjectInfos.sort((a, b) => a.object.order - b.object.order);
    
    setObjectInfos(newObjectInfos, newObjectInfo, null, null, sharedObjectInfos);
    updateStorageSelection(workspace?._key, newObjectInfo.object?._key, null, null);
    setIsPopoverOpen(false);
  }

  const closeMenu = () => {
    setIsPopoverOpen(false);
  }

  function openMenu(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    setIsPopoverOpen(true);
  }

  return (
    <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <PopoverTrigger className={TW_ObjectMenuButton} onClick={openMenu}>
          <EllipsisVertical className={TW_ObjectMenuButtonIcon + (showIcon ? " text-gray-400" : " text-white")} />
      </PopoverTrigger>
      <PopoverContent className="w-max p-1">
        <ul>
          {canEditObject && <li><ObjectMenuEditItem objectInfo={objectInfo} onObjectUpdate={objectUpdated} closeMenu={closeMenu} /></li>}
          {canEditObject && <li><ObjectMenuDuplicateItem objectInfo={objectInfo} onObjectUpdate={duplicateObject} closeMenu={closeMenu} /></li>}
          {canEditObject && <li><ObjectMenuDeleteItem objectInfo={objectInfo} onObjectDelete={deleteObject} closeMenu={closeMenu} /></li>}
          <li><ObjectMenuAddFolderItem objectKey={objectInfo.object._key} closeMenu={closeMenu} /></li>
        </ul>
      </PopoverContent>
    </Popover>
  )
}

export default ObjectMenuButton;
